import { useState, useMemo } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button, Icon } from "@elaniintc/goes-design-system/ui";
import { PulseIndicator } from "../pulse-indicator";
import type { Notification } from "./types";
import {
  useGetNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
} from "@/queries/notifications";
import { groupNotifications, transformApiNotifications } from "./utils";
import { SectionDivider } from "./section-divider";
import { NotificationCard } from "./notification-card";
import { EmptyState } from "./empty-state";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { useMarkAsUnread } from "@/queries/notifications/notifications.api";

/**
 * Creates custom description for signature-error notifications
 */
const createCustomDescription = (notification: Notification) => {
  // Check for failed signature with file_name
  if (
    notification.metadata?.document_was_signed === false &&
    notification.metadata?.file_name
  ) {
    return (
      <span>
        La firma de un documento en{" "}
        <span className="text-blue-700 font-bold">
          {notification.metadata?.node_name as string}
        </span>{" "}
        en la solicitud{" "}
        <span className="text-blue-700 font-bold">
          {notification.metadata?.formality_code as string} (
          {notification.metadata?.process_name as string})
        </span>{" "}
        no pudo ser completada, por favor vuelva a intentarlo.
      </span>
    );
  }
  // Keep existing signature-error check for backward compatibility
  if (notification.detail?.type === "signature-error") {
    return (
      <span>
        La firma de un documento en{" "}
        <span className="text-blue-700 font-bold">
          {notification.metadata?.node_name as string}
        </span>{" "}
        en la solicitud{" "}
        <span className="text-blue-700 font-bold">
          {notification.metadata?.formality_code as string} (
          {notification.metadata?.process_name as string})
        </span>{" "}
        no pudo ser completada, por favor vuelva a intentarlo.
      </span>
    );
  }
  return null;
};

/**
 * Notifications list component with grouped sections.
 */
const NotificationsList = ({
  notifications,
  onToggleRead,
  onToggleUnread,
  isLoading,
}: {
  notifications: Notification[];
  onToggleRead: (id: string) => void;
  onToggleUnread: (id: string) => void;
  isLoading?: boolean;
}) => {
  const { now, yesterday, past } = groupNotifications(notifications);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Icon
          type="fas"
          name="spinner-third"
          className="text-action animate-spin text-xl"
        />
      </div>
    );
  }

  if (notifications.length === 0) {
    return <EmptyState />;
  }

  return (
    <>
      {/* Now section */}
      {now.length > 0 && (
        <>
          <SectionDivider label="Ahora" />
          {now.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onToggleRead={onToggleRead}
              onToggleUnread={onToggleUnread}
              customDescription={createCustomDescription(notification)}
            />
          ))}
        </>
      )}

      {/* Yesterday section */}
      {yesterday.length > 0 && (
        <>
          <SectionDivider label="Ayer" />
          {yesterday.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onToggleRead={onToggleRead}
              onToggleUnread={onToggleUnread}
              customDescription={createCustomDescription(notification)}
            />
          ))}
        </>
      )}

      {/* Past section */}
      {past.length > 0 && (
        <>
          <SectionDivider label="Pasados" />
          {past.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onToggleRead={onToggleRead}
              onToggleUnread={onToggleUnread}
              customDescription={createCustomDescription(notification)}
            />
          ))}
        </>
      )}
    </>
  );
};

export const NotificationsPopover = () => {
  const [open, setOpen] = useState(false);
  const { hasPermission } = useAuthorization();
  const canReadNotifications = hasPermission(Permissions.notifications.read);

  // Fetch notifications from API
  const {
    data: notificationsResponse,
    isLoading,
    refetch,
  } = useGetNotifications(
    { per_page: 50 },
    {
      enabled: canReadNotifications,
      // refetchInterval: 30000 //TODO: Enable for real-time updates when API supports it
    }, // Refetch every 30 seconds
  );

  // Mutations
  const markAsReadMutation = useMarkAsRead();
  const markAsUnreadMutation = useMarkAsUnread();
  const markAllAsReadMutation = useMarkAllAsRead();

  // Transform API data to component format
  const notifications = useMemo(() => {
    if (!notificationsResponse?.data) return [];

    const transformedNotifications = transformApiNotifications(
      notificationsResponse.data,
    );

    return transformedNotifications;
  }, [notificationsResponse?.data]);

  // Get unread count from API meta
  const unreadCount = notificationsResponse?.meta?.unread_count ?? 0;
  const hasUnread = unreadCount > 0;

  const handleToggleRead = async (id: string) => {
    try {
      await markAsReadMutation.mutateAsync(id);
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleToggleUnread = async (id: string) => {
    try {
      await markAsUnreadMutation.mutateAsync(id);
    } catch (error) {
      console.error("Error marking notification as unread:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsReadMutation.mutateAsync();
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  // Refetch when popover opens
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      refetch();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="quiet"
          size="sm"
          className="size-9 relative"
          aria-label="Notificaciones"
          data-testid="notifications-trigger"
        >
          <Icon type="far" name="bell" className="text-current text-sm" />
          <PulseIndicator showIndicator={hasUnread} />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-105 max-h-150 overflow-hidden p-0 bg-neutral-object border-neutral-silent rounded-xl shadow-neutral-lg"
        align="end"
        sideOffset={8}
        data-testid="notifications-popover"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3">
          <h3 className="text-heading font-semibold text-lg leading-snug">
            Notificaciones
            {unreadCount > 0 && (
              <span className="ml-2 text-sm font-normal text-body-quiet">
                ({unreadCount} sin leer)
              </span>
            )}
          </h3>
          {unreadCount > 0 && (
            <Button
              variant="quiet"
              size="xs"
              className="shrink-0"
              onClick={handleMarkAllAsRead}
              disabled={markAllAsReadMutation.isPending}
            >
              {markAllAsReadMutation.isPending ? (
                <Icon
                  type="fas"
                  name="spinner-third"
                  className="animate-spin"
                />
              ) : (
                "Marcar como leído"
              )}
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto p-2 pb-4 flex flex-col gap-2 max-h-130">
          <NotificationsList
            notifications={notifications}
            onToggleRead={handleToggleRead}
            onToggleUnread={handleToggleUnread}
            isLoading={isLoading}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};
